import {
  AppConfig,
  useSubscriptionAccessConfigContext,
} from '@ltvco/refresh-lib/ctx';
import { Text, Stack, useTheme, Box } from '@ltvco/refresh-lib/theme';
import { scrollToElement } from '@ltvco/refresh-lib/utils';
import {
  useDarkWebReport,
  useTuxedo,
  useSession,
  LoadingDots,
} from '@ltvco/refresh-lib/v1';
import {
  FeatureBlockedModal,
  useFeatureBlockedModal,
} from '@ltvco/refresh-lib/v2';
import { CheckCircle } from '@mui/icons-material';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { StyledSettingsCard, StyledButton, StyledStack } from './components';
import { defaultFalseDataSet } from './constants';
import { buildSettings, Setting } from './utils';

export const SettingsTiles = () => {
  const navigate = useNavigate();
  const { homeAdditions } = useTuxedo();
  const { trackEvent } = useContext(AppConfig);
  const { isBlocked, isHidden } = useSubscriptionAccessConfigContext();
  const {
    isFeatureBlockedModalOpen,
    openFeatureBlockedModal,
    closeFeatureBlockedModal,
  } = useFeatureBlockedModal();

  const [searchType, setSearchType] = useState<string>('');
  const [paymentShowModalNumber, setPaymentShowModalNumber] =
    useState<number>(0);

  const {
    session: { account },
  } = useSession();

  const {
    data: homeAdditionsData,
    isFetching: isHomeAdditionsLoading,
    isError: isHomeAdditionsError,
  } = homeAdditions(null);

  const { data: darkWebData, isLoading } = useDarkWebReport({
    email: account?.account.user_info?.email as string,
  });

  const theme = useTheme();

  // Function to scroll ID Monitor container into view
  const scrollIdMonitorIntoView = () => {
    scrollToElement('id-monitor-container');
  };

  if (isLoading || isHomeAdditionsLoading)
    return (
      <>
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            padding: [2.5],
          }}
        >
          <Text variant="h3">Identity Protection Features & Services</Text>
          <Text variant="body1" mb={2.5}>
            Take advantage of identity and credit features and services that can
            help empower and protect you against fraudulent activity.
          </Text>
          <LoadingDots count={4} />
        </Box>
      </>
    );

  let settings: Setting[] = [];
  const isEnrolled =
    account?.account.user_settings.credit_hub_enrollment_completed;
  const darkWebUpdatedAt = darkWebData?.data?.rawData?.meta?.updated_at;
  const hasDataSets = homeAdditionsData?.user_data_sets;
  const hasCreditHubActionDataSet =
    hasDataSets?.user_credit_hub_action_data_set;
  if (hasCreditHubActionDataSet && darkWebUpdatedAt) {
    const dataSet =
      homeAdditionsData.user_data_sets.user_credit_hub_action_data_set;
    settings = buildSettings(dataSet, darkWebUpdatedAt, !!isEnrolled);
  }

  if (
    (isHomeAdditionsError || (hasDataSets && !hasCreditHubActionDataSet)) &&
    darkWebUpdatedAt
  ) {
    settings = buildSettings(
      defaultFalseDataSet,
      darkWebUpdatedAt,
      !!isEnrolled
    );
  }

  const monitorTabToSearchType: Record<string, string> = {
    'Dark Web Scan': 'dark_web',
    'Unclaimed Money': 'unclaimed_money',
  };

  settings = settings.filter(
    (setting) => !isHidden(monitorTabToSearchType[setting.title] || '')
  );

  const displaySettingsRow = (settings: Setting[]) => {
    return settings.map((setting) => (
      <StyledSettingsCard>
        <Stack gap={1.5}>
          <Stack direction="row" justifyContent={'space-between'}>
            <img src={setting.icon} alt={setting.altIcon} />
            {setting.enrolled !== undefined && (
              <Stack direction="row">
                {setting.enrolled && (
                  <CheckCircle
                    sx={{
                      color: '#81B44C',
                      width: '13px',
                      height: '13px',
                      marginRight: 0.5,
                    }}
                  />
                )}
                <Text variant="body2">
                  {setting.enrolled
                    ? setting.enrolledText
                    : setting.notEnrolledText}
                </Text>
              </Stack>
            )}
          </Stack>
          <Stack gap={1}>
            <Text variant="h6" mb={0}>
              {setting.title}
            </Text>
            <Text variant="body1">
              {setting.enrolled !== undefined && setting.enrolled
                ? setting.enrolledDescription
                : setting.description}
            </Text>
          </Stack>
        </Stack>
        <StyledButton
          theme={theme}
          variant={setting.enrolled ? 'outlined' : 'contained'}
          fullWidth
          onClick={() => {
            trackEvent(
              setting.gaCategory,
              'navigation click',
              'id monitor settings'
            );
            if (setting.activateMonitorTab) {
              scrollIdMonitorIntoView();
            } else if (setting.navigateTo) {
              if (isBlocked(monitorTabToSearchType[setting.title])) {
                setSearchType(setting.title);
                openFeatureBlockedModal();
                return;
              }

              navigate(setting.navigateTo);
            }
          }}
        >
          {setting.enrolled ? setting.enrolledBtnText : setting.btnText}
        </StyledButton>
      </StyledSettingsCard>
    ));
  };

  return (
    <>
      <Box
        sx={{ backgroundColor: theme.palette.background.paper, padding: [2.5] }}
      >
        <Text variant="h3">Identity Protection Features & Services</Text>
        <Text variant="body1">
          Take advantage of identity and credit features and services that can
          help empower and protect you against fraudulent activity.
        </Text>
        <StyledStack direction={'row'}>
          {displaySettingsRow(settings)}
        </StyledStack>
      </Box>

      <FeatureBlockedModal
        isOpen={isFeatureBlockedModalOpen}
        onClose={closeFeatureBlockedModal}
        searchType={searchType}
        paymentShowModalNumber={paymentShowModalNumber}
        setPaymentShowModalNumber={setPaymentShowModalNumber}
      />
    </>
  );
};
